import { tipoImovelTextRegistry, conjuge } from "../dynamicTextRegistry";
import { maskCpf, maskPhoneBr } from "../../utils/masks";
import { formatBrlCurrencyForDoc } from "../../utils/brFormat";

function formatCpfPreview(value) {
  return maskCpf(value ?? "");
}

function formatPhonePreview(value) {
  return maskPhoneBr(value ?? "");
}

function formatDateBR(value) {
  if (!value) return "";
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    if (!Number.isNaN(dt.getTime())) return dt.toLocaleDateString("pt-BR");
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return s;
  return parsed.toLocaleDateString("pt-BR");
}

function escapeHtml(value) {
  if (value == null) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function strongLabel(text) {
  return `<strong>${escapeHtml(text)}</strong>`;
}

function replacePlaceholdersInStringEscaped(template, values) {
  if (!template) return "";
  return template.replace(/\{(.*?)\}/g, (_, key) =>
    escapeHtml(values[key] ?? "")
  );
}

function boldConjugeTemplateLabels(html) {
  return html
    .replace(/CÔNJUGE:/g, `${strongLabel("CÔNJUGE:")}`)
    .replace(/\bCPF:/g, `${strongLabel("CPF:")}`)
    .replace(/\bRG:/g, `${strongLabel("RG:")}`)
    .replace(/NACIONALIDADE:/g, `${strongLabel("NACIONALIDADE:")}`)
    .replace(/PROFISSÃO:/g, `${strongLabel("PROFISSÃO:")}`)
    .replace(/\bEMAIL:/g, `${strongLabel("EMAIL:")}`);
}

function renderConjugeBlockHtml(person) {
  if (person?.ESTADO_CIVIL_PESSOA_FISICA !== "casado") return "";
  const tpl = conjuge.casado;
  if (!tpl) return "";
  const valuesForPreview = {
    ...person,
    CPF_CONJUGE: formatCpfPreview(person.CPF_CONJUGE),
  };
  const filled = replacePlaceholdersInStringEscaped(tpl, valuesForPreview).trim();
  if (!filled) return "";
  return boldConjugeTemplateLabels(filled);
}

function buildPessoaFisicaBlockHtml(person) {
  const parts = [
    `${strongLabel("NOME:")} ${escapeHtml(person.NOME_PESSOA_FISICA)}`,
    `${strongLabel("CPF:")} ${escapeHtml(formatCpfPreview(person.CPF_PESSOA_FISICA))}`,
    `${strongLabel("RG:")} ${escapeHtml(person.RG_PESSOA_FISICA)}`,
    `${strongLabel("NACIONALIDADE:")} ${escapeHtml(
      person.NACIONALIDADE_PESSOA_FISICA
    )}`,
    `${strongLabel("PROFISSÃO:")} ${escapeHtml(person.PROFISSAO_PESSOA_FISICA)}`,
    `${strongLabel("ESTADO CIVIL:")} ${escapeHtml(
      person.ESTADO_CIVIL_PESSOA_FISICA
    )}`,
  ];
  const conj = renderConjugeBlockHtml(person);
  if (conj) parts.push(conj);
  parts.push(
    `${strongLabel("ENDEREÇO:")} ${escapeHtml(
      person.RUA_ENDERECO_PESSOA_FISICA
    )}, ${strongLabel("nº")} ${escapeHtml(
      person.N_ENDERECO_PESSOA_FISICA
    )}, ${strongLabel("BAIRRO")} ${escapeHtml(
      person.BAIRRO_ENDERECO_PESSOA_FISICA
    )}, ${escapeHtml(person.MUN_ENDERECO_PESSOA_FISICA)} ${strongLabel(
      "- CEP:"
    )} ${escapeHtml(person.CEP_PESSOA_FISICA)}`,
    `${strongLabel("EMAIL:")} ${escapeHtml(person.EMAIL_PESSOA_FISICA)}`,
    `${strongLabel("TELEFONE:")} ${escapeHtml(
      formatPhonePreview(person.TEL_PESSOA_FISICA)
    )}`
  );
  return parts.join(" ");
}

function signatureBlock(name, cpf) {
  return `________________________\n${name ?? ""}\n${formatCpfPreview(cpf)}`;
}

function signatureBlockConjugeHtml(name, cpf) {
  const nameEsc = escapeHtml(name ?? "");
  const cpfEsc = escapeHtml(formatCpfPreview(cpf));
  return `<div class="signature-conjuge-block space-y-1 mb-1"><div>________________________</div><div>${nameEsc}</div><div>${cpfEsc}</div></div>`;
}

export function propostaCompraImovelResolver(formData, userConfig) {
  const safeUserConfig = userConfig || {};
  const data = { ...safeUserConfig, ...formData };

  data.ENDERECO_IMOBILIARIA =
    formData.ENDERECO_IMOBILIARIA !== undefined &&
    formData.ENDERECO_IMOBILIARIA !== null
      ? formData.ENDERECO_IMOBILIARIA
      : safeUserConfig.endereco_imobiliaria || "";
  data.CELULAR_IMOBILIARIA = formatPhonePreview(
    formData.CELULAR_IMOBILIARIA !== undefined &&
      formData.CELULAR_IMOBILIARIA !== null
      ? formData.CELULAR_IMOBILIARIA
      : safeUserConfig.celular_imobiliaria || ""
  );
  data.EMAIL_IMOBILIARIA =
    formData.EMAIL_IMOBILIARIA !== undefined &&
    formData.EMAIL_IMOBILIARIA !== null
      ? formData.EMAIL_IMOBILIARIA
      : safeUserConfig.email_imobiliaria || "";
  data.CRECI_IMOBILIARIA =
    formData.CRECI_IMOBILIARIA !== undefined &&
    formData.CRECI_IMOBILIARIA !== null
      ? formData.CRECI_IMOBILIARIA
      : safeUserConfig.creci_imobiliaria || "";
  data.SITE_IMOBILIARIA =
    formData.SITE_IMOBILIARIA !== undefined &&
    formData.SITE_IMOBILIARIA !== null
      ? formData.SITE_IMOBILIARIA
      : safeUserConfig.site_imobiliaria || "";

  if (formData.vendedores?.length) {
    data.VENDEDORES = formData.vendedores
      .map((v) => buildPessoaFisicaBlockHtml(v))
      .join("<br /><br />");

    data.ASSINATURA_VENDEDORES = formData.vendedores
      .map((v) =>
        signatureBlock(v.NOME_PESSOA_FISICA, v.CPF_PESSOA_FISICA)
      )
      .join("\n\n");
    data.ASSINATURA_CONJUGE_VENDEDORES = formData.vendedores
      .filter((v) => v.ESTADO_CIVIL_PESSOA_FISICA === "casado")
      .map((v) => signatureBlockConjugeHtml(v.NOME_CONJUGE, v.CPF_CONJUGE))
      .join('<div class="h-4" aria-hidden="true"></div>');
  }

  if (formData.compradores?.length) {
    data.COMPRADORES = formData.compradores
      .map((c) => buildPessoaFisicaBlockHtml(c))
      .join("<br /><br />");

    data.ASSINATURA_COMPRADORES = formData.compradores
      .map((c) =>
        signatureBlock(c.NOME_PESSOA_FISICA, c.CPF_PESSOA_FISICA)
      )
      .join("\n\n");
    data.ASSINATURA_CONJUGE_COMPRADORES = formData.compradores
      .filter((c) => c.ESTADO_CIVIL_PESSOA_FISICA === "casado")
      .map((c) => signatureBlockConjugeHtml(c.NOME_CONJUGE, c.CPF_CONJUGE))
      .join('<div class="h-4" aria-hidden="true"></div>');
  }

  if (formData.testemunhas?.length) {
    const joined = formData.testemunhas
      .map((t) =>
        signatureBlock(t.NOME_TESTEMUNHA, t.CPF_TESTEMUNHA)
      )
      .join("\n\n");
    data.ASSINATURA_TESTEMUNHAS = joined;
    data.ASSINATURA_TESTEMUNHA = joined;
  }

  if (formData.tipo_imovel?.type) {
    const templateText = tipoImovelTextRegistry[formData.tipo_imovel.type];
    data.TIPO_IMOVEL = templateText?.replace(
      /\{(.*?)\}/g,
      (_, key) => formData.tipo_imovel.fields?.[key] || ""
    );
  }

  if (formData.VALOR_TOTAL != null && formData.VALOR_TOTAL !== "") {
    data.VALOR_TOTAL = formatBrlCurrencyForDoc(formData.VALOR_TOTAL);
  }
  if (formData.VALOR_EXTENSO != null) {
    data.VALOR_EXTENSO = String(formData.VALOR_EXTENSO);
  }

  if (formData.VALIDADE_PROPOSTA) {
    data.VALIDADE_PROPOSTA = formatDateBR(formData.VALIDADE_PROPOSTA);
  }

  if (formData.PRAZO_CONTRATO != null && formData.PRAZO_CONTRATO !== "") {
    const n = Number(formData.PRAZO_CONTRATO);
    data.PRAZO_CONTRATO = Number.isFinite(n)
      ? String(Math.trunc(n))
      : String(formData.PRAZO_CONTRATO);
  }

  if (formData.FORO != null) {
    data.FORO = String(formData.FORO);
  }

  if (formData.tipo_pagamento != null && formData.tipo_pagamento !== "") {
    data.TIPO_PAGAMENTO = String(formData.tipo_pagamento);
  }

  if (data.DATA_ASSINATURA) {
    data.DATA_ASSINATURA = formatDateBR(data.DATA_ASSINATURA);
  }

  return data;
}
